import { DEFAULT_STORE_OBJECT } from '@/redux/constants';
import type { DefaultStoreObject } from '@/redux/types';
import type { ProcurementProject, ProcurementUser } from '@/views/screens/Procurement';
import ProcurementProjectType from './ProcurementProjectTypes';
import getCreateUpdateProjectReq from './requestPayload/GetCreateUpdateProject';
import getProjectCopy from './requestPayload/GetProjectCopy';
import getProjectListBySearchReq from './requestPayload/GetProjectListBySearch';
import getTendersByProjectReq from './requestPayload/GetTendersByProjectReq';
import getUserProcurement from './requestPayload/GetUserProcurement';

export interface ProcurementProjectState extends Record<string, any> {
  projectList: DefaultStoreObject<ProcurementProject[]> & {
    req: Record<string, any>;
    totalActive?: number;
    totalApproved?: number;
    totalCount?: number;
  };
  userProcurement: DefaultStoreObject<Partial<Record<number, ProcurementUser[]>>> & {
    req: Record<string, any>;
  };
  selectedProject: ProcurementProject | null;
  activeProject: {
    data: ProcurementProject;
  };
}

const initialState: ProcurementProjectState = {
  projectList: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    req: {
      ...getProjectListBySearchReq,
    },
  },
  checkProjectName: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
  },
  userProcurement: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    req: {
      ...getUserProcurement,
    },
  },
  tenderByProjectList: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    req: {
      ...getTendersByProjectReq,
    },
  },
  createUpdateProject: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    req: {
      ...getCreateUpdateProjectReq,
    },
  },
  projectCopy: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    req: {
      ...getProjectCopy,
    },
  },
  activeProject: {
    data: {} as ProcurementProject,
  },
  selectedProject: null,
};

// biome-ignore lint/style/useDefaultParameterLast: <explanation>
const ProcurementProjectReducer = (state = initialState, { type, payload }: any): ProcurementProjectState => {
  switch (type) {
    case ProcurementProjectType.GET_PROCUREMENT_PROJECT_LIST: {
      return {
        ...state,
        projectList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.projectList,
          req: {
            ...state.projectList.req,
            ...payload,
          },
          progressing: true,
        },
      };
    }

    case ProcurementProjectType.PROCUREMENT_PROJECT_LIST_UPDATE: {
      return {
        ...state,
        projectList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.projectList,
          data: payload.data,
        },
      };
    }

    case ProcurementProjectType.GET_PROCUREMENT_PROJECT_LIST_SUCCESS: {
      return {
        ...state,
        projectList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.projectList,
          success: true,
          data: [...(state.projectList.data || []), ...payload.data.result],
          totalActive: payload.data.totalActive,
          totalApproved: payload.data.totalApproved,
          totalCount: payload.data.totalCount,
          progressing: false,
        },
      };
    }

    case ProcurementProjectType.GET_PROCUREMENT_PROJECT_LIST_FAIL: {
      return {
        ...state,
        projectList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.projectList,
          error: true,
          data: payload.data,
          progressing: false,
          totalActive: 0,
          totalApproved: 0,
          totalCount: 0,
        },
      };
    }

    case ProcurementProjectType.GET_PROCUREMENT_PROJECT_LIST_REQUEST_RESET: {
      return {
        ...state,
        projectList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.projectList,
          req: {
            ...getProjectListBySearchReq,
          },
          data: [],
          totalActive: 0,
          totalApproved: 0,
          totalCount: 0,
        },
      };
    }

    case ProcurementProjectType.UPDATE_PROCUREMENT_PROJECT_INFO_REQUEST: {
      return {
        ...state,
        req: {
          ...state.getProjectListBySearchReq,
          ...payload,
        },
      };
    }

    case ProcurementProjectType.CHECK_PROJECT_NAME_IS_EXISTS: {
      return {
        ...state,
        checkProjectName: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }

    case ProcurementProjectType.CHECK_PROJECT_NAME_IS_EXISTS_SUCCESS: {
      return {
        ...state,
        checkProjectName: {
          ...DEFAULT_STORE_OBJECT,
          ...state.checkProjectName,
          success: true,
          data: payload.data,
          progressing: false,
        },
      };
    }

    case ProcurementProjectType.CHECK_PROJECT_NAME_IS_EXISTS_FAIL: {
      return {
        ...state,
        checkProjectName: {
          ...DEFAULT_STORE_OBJECT,
          ...state.checkProjectName,
          error: true,
          data: payload.data,
          progressing: false,
        },
      };
    }
    /** User Procurement Reducer start */

    case ProcurementProjectType.GET_USER_PROCUREMENT: {
      return {
        ...state,
        userProcurement: {
          ...DEFAULT_STORE_OBJECT,
          ...state.userProcurement,
          req: {
            ...state.userProcurement.req,
            ...payload,
          },
          progressing: true,
        },
      };
    }

    case ProcurementProjectType.GET_USER_PROCUREMENT_SUCCESS: {
      // @ts-ignore
      const result = Object.groupBy(payload?.data as ProcurementUser[], ({ groupId }) => groupId);

      return {
        ...state,
        userProcurement: {
          ...DEFAULT_STORE_OBJECT,
          ...state.userProcurement,
          success: true,
          data: result,
          progressing: false,
        },
      };
    }

    case ProcurementProjectType.GET_USER_PROCUREMENT_FAIL: {
      return {
        ...state,
        userProcurement: {
          ...DEFAULT_STORE_OBJECT,
          ...state.userProcurement,
          error: true,
          data: payload.data,
          progressing: false,
        },
      };
    }

    case ProcurementProjectType.GET_USER_PROCUREMENT_REQUEST_RESET: {
      return {
        ...state,
        userProcurement: {
          ...DEFAULT_STORE_OBJECT,
          ...state.userProcurement,
          req: {
            ...getUserProcurement,
          },
          data: [],
        },
      };
    }
    /** User Procurement Reducer  end */

    /** Get tender by project list reducer start */
    case ProcurementProjectType.GET_TENDER_BY_PROJECT_LIST: {
      return {
        ...state,
        tenderByProjectList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.tenderByProjectList,
          req: {
            ...state.tenderByProjectList.req,
            ...payload,
          },
          progressing: true,
        },
      };
    }

    case ProcurementProjectType.GET_TENDER_BY_PROJECT_LIST_SUCCESS: {
      return {
        ...state,
        tenderByProjectList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.tenderByProjectList,
          success: true,
          data: payload.data,
          progressing: false,
        },
      };
    }

    case ProcurementProjectType.GET_TENDER_BY_PROJECT_LIST_FAIL: {
      return {
        ...state,
        tenderByProjectList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.tenderByProjectList,
          error: true,
          data: payload.data,
          progressing: false,
        },
      };
    }

    case ProcurementProjectType.GET_TENDER_BY_PROJECT_LIST_REQUEST_RESET: {
      return {
        ...state,
        tenderByProjectList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.tenderByProjectList,
          req: {
            ...getTendersByProjectReq,
          },
          data: [],
        },
      };
    }
    /** Get tender by project list reducer  end */

    /** Create and Update reducer start */
    case ProcurementProjectType.CREATE_UPDATE_PROJECT: {
      return {
        ...state,
        createUpdateProject: {
          ...DEFAULT_STORE_OBJECT,
          req: {
            ...state.createUpdateProject.req,
            ...payload,
          },
          progressing: true,
        },
      };
    }

    case ProcurementProjectType.CREATE_UPDATE_PROJECT_SUCCESS: {
      return {
        ...state,
        createUpdateProject: {
          ...DEFAULT_STORE_OBJECT,
          ...state.createUpdateProject,
          error: false,
          success: true,
          data: payload.data,
          progressing: false,
        },
      };
    }

    case ProcurementProjectType.CREATE_UPDATE_PROJECT_FAIL: {
      return {
        ...state,
        createUpdateProject: {
          ...DEFAULT_STORE_OBJECT,
          ...state.createUpdateProject,
          error: true,
          data: payload,
          success: false,
          progressing: false,
        },
      };
    }

    case ProcurementProjectType.CREATE_UPDATE_PROJECT_REQUEST_RESET: {
      return {
        ...state,
        createUpdateProject: {
          ...DEFAULT_STORE_OBJECT,
          ...state.createUpdateProject,
          req: {
            ...getCreateUpdateProjectReq,
          },
          success: false,
          error: false,
          data: [],
        },
      };
    }
    /**  Create and Update reducer  end */

    /** Project Copy reducer start */
    case ProcurementProjectType.CREATE_PROJECT_COPY: {
      return {
        ...state,
        projectCopy: {
          ...DEFAULT_STORE_OBJECT,
          req: {
            ...state.projectCopy.req,
            ...payload,
          },
          progressing: true,
        },
      };
    }

    case ProcurementProjectType.CREATE_PROJECT_COPY_SUCCESS: {
      return {
        ...state,
        projectCopy: {
          ...DEFAULT_STORE_OBJECT,
          ...state.projectCopy,
          error: false,
          success: true,
          data: payload.data,
          progressing: false,
        },
      };
    }

    case ProcurementProjectType.CREATE_PROJECT_COPY_FAIL: {
      return {
        ...state,
        projectCopy: {
          ...DEFAULT_STORE_OBJECT,
          ...state.projectCopy,
          error: true,
          data: payload,
          success: false,
          progressing: false,
        },
      };
    }

    case ProcurementProjectType.CREATE_PROJECT_COPY_RESET: {
      return {
        ...state,
        projectCopy: {
          ...DEFAULT_STORE_OBJECT,
          ...state.projectCopy,
          req: {
            ...getProjectCopy,
          },
          success: false,
          error: false,
          data: [],
        },
      };
    }
    /**  Project Copy reducer  end */

    /** Active Project Item set and reset reducer start */

    case ProcurementProjectType.SET_ACTIVE_PROJECT_ITEM: {
      return {
        ...state,
        activeProject: {
          data: payload,
        },
      };
    }

    case ProcurementProjectType.RESET_ACTIVE_PROJECT_ITEM: {
      return {
        ...state,
        activeProject: {
          data: {} as ProcurementProject,
        },
      };
    }

    case ProcurementProjectType.SET_SELECTED_PROJECT_ACTION: {
      return {
        ...state,
        selectedProject: payload,
      };
    }
    /** Active Project Item set and reset reducer end */

    default:
      return state;
  }
};

export default ProcurementProjectReducer;
